<script setup lang="ts" generic="T extends MultiSelectOption">
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from 'radix-vue'
import * as R from 'ramda'
import { computed, ref, watch } from 'vue'

import { vRequired } from '../../directives'
import Checkbox from '../Checkbox/Checkbox.vue'
import FormControl from '../FormControl.vue'
import Icon from '../Icon/Icon.vue'
import {
  hasNewDisabledSelection,
  isOptionDisabled,
  isSearchTermIncludedInOption,
  isStringOption,
} from './multi-select.utils'
import MultiSelectClearButton from './MultiSelectClearButton.vue'
import {
  MultiSelectObjectOption,
  MultiSelectOption,
  MultiSelectOptionValue,
  MultiSelectValue,
} from './types'

type ComboboxModelValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | (string | number | boolean | Record<string, unknown>)[]
  | undefined

type Props = {
  withClear?: boolean | 'option'
  withEmptyOption?: boolean
  inputClass?: string
  labelClass?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  mode?: 'select' | 'combobox'
  options: T[] | Promise<T[]>
  withFilter?: boolean
  withOptionsFullWidth?: boolean
  filterPredicate?: (
    option: MultiSelectObjectOption | string,
    searchTerm: string,
  ) => boolean
  selectionConstraint?: (
    newSelection: MultiSelectOptionValue[],
    previousSelection: MultiSelectOptionValue[],
  ) => MultiSelectOptionValue[]
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'select',
  placeholder: 'select',
})

// emits
type Emits = {
  /**
   * after selected value is cleared, but only if the component is not open - otherwise, it emits blur
   */
  clearValue: []
  blur: []
}

const emit = defineEmits<Emits>()

// data
const optionsWidthStyle = props.withOptionsFullWidth
  ? ''
  : 'width: calc(var(--radix-combobox-trigger-width) - 2px)'

const comboboxItemClass =
  'data-[highlighted]:bg-neutral-light-5 data-[disabled]:text-neutral-dark-0/50 flex h-8 cursor-pointer select-none items-center px-1.5 leading-4 data-[disabled]:pointer-events-none data-[highlighted]:outline-hidden'

// state
const pendingOptions = ref<Promise<T[]>>()
const resolvedOptions = ref<T[]>([])

// models
const searchTerm = defineModel<string>('searchTerm', { default: '' })
const open = defineModel<boolean>('open', { default: false })

/* default null - single select */
const selected = defineModel<MultiSelectValue>({ default: null })

// methods
function clearSelectedValue(): void {
  if (Array.isArray(selected.value)) {
    selected.value = []
  } else if (R.is(String, selected.value)) {
    selected.value = ''
  } else {
    selected.value = undefined
  }
}

function clearValue(): void {
  clearSelectedValue()
  searchTerm.value = ''

  if (open.value) {
    open.value = false
  } else {
    emit('clearValue')
  }
}

function getOptionName(option: MultiSelectOption): string {
  if (isStringOption(option)) {
    return option
  }

  return option.name
}

function getOptionValue(option: MultiSelectOption): MultiSelectOptionValue {
  if (isStringOption(option)) {
    return option
  }

  return option.value
}

function getOptionTextValue(option: MultiSelectOption): string {
  if (isStringOption(option)) {
    return option
  }

  return option.name
}

function hasSelectedOption(option: MultiSelectOption): boolean {
  const value = getOptionValue(option)

  if (Array.isArray(selected.value)) {
    return R.includes(value, selected.value)
  }

  return R.equals(value, selected.value)
}

function findOption(
  value: MultiSelectOptionValue,
): MultiSelectOption | undefined {
  return resolvedOptions.value.find((option) => {
    if (isStringOption(option)) {
      return option === value
    }

    return R.equals(option.value, value)
  })
}

function handleSelectionUpdate(newVal: MultiSelectValue): void {
  if (hasNewDisabledSelection(newVal, selected.value, resolvedOptions.value)) {
    return
  }

  if (props.selectionConstraint && Array.isArray(newVal)) {
    const previousVal = Array.isArray(selected.value) ? selected.value : []
    selected.value = props.selectionConstraint(newVal, previousVal)
  } else {
    selected.value = newVal
  }
}

function updateSearchTerm(value: string): void {
  /** on mode=combobox, prevent resetting searchTerm after value is selected, to allow re-select options */
  if (props.mode === 'select' || isSelectedEmptyValue.value) {
    searchTerm.value = value
  }
}

const isSelectedEmptyValue = computed(() => {
  return (
    selected.value === '' ||
    selected.value === undefined ||
    selected.value === null
  )
})

const isModeMultiple = computed(() => Array.isArray(selected.value))

const comboboxModelValue = computed(() => selected.value as ComboboxModelValue)

// computed
const filteredOptions = computed(() => {
  if (searchTerm.value === '' || props.mode === 'combobox')
    return resolvedOptions.value

  const predicate = props.filterPredicate ?? isSearchTermIncludedInOption

  return resolvedOptions.value.filter((option) =>
    predicate(option, searchTerm.value),
  )
})

const displayedValue = computed(() => {
  if (Array.isArray(selected.value) && selected.value.length > 1) {
    return `${selected.value.length} selected`
  }

  return selectedOption.value ? getOptionName(selectedOption.value) : ''
})

const selectedOption = computed(() => {
  if (Array.isArray(selected.value) && selected.value.length > 1) {
    return null // multiple selected options not implemented yet (not used)
  }

  const selectedValue = Array.isArray(selected.value)
    ? selected.value[0]
    : selected.value

  if (
    selectedValue === '' ||
    selectedValue === undefined ||
    selectedValue === null
  ) {
    return null
  }

  return findOption(selectedValue)
})

const emptyText = computed(() => {
  if (props.mode === 'combobox' || resolvedOptions.value.length > 0) {
    return 'No results found'
  }

  return 'No results available'
})

// init
watch(open, (newVal) => {
  if (newVal === false) {
    emit('blur')
  }
})

watch(
  () => props.options,
  (newVal) => {
    if (Array.isArray(newVal)) {
      resolvedOptions.value = newVal
    } else {
      pendingOptions.value = newVal

      newVal.then((options) => {
        // ensure that resolved options are last promise we are waiting for
        // (prevents showing outdated options from previous promise if it will resolve after the current one)
        if (pendingOptions.value === newVal) {
          pendingOptions.value = undefined
          resolvedOptions.value = options
        }
      })
    }
  },
  { immediate: true },
)
</script>

<template>
  <FormControl
    :disabled
    :labelClass
  >
    <template #label>
      <slot />
    </template>
    <ComboboxRoot
      v-model:open="open"
      :class="inputClass ? inputClass : 'w-full'"
      :modelValue="comboboxModelValue"
      :disabled
      :multiple="true"
      :resetSearchTermOnBlur="false"
      @update:modelValue="handleSelectionUpdate"
      @update:searchTerm="updateSearchTerm"
    >
      <!-- :multiple="true" is needed to display all options to choose when one is already selected -->
      <ComboboxAnchor
        class="inline-flex h-8 w-full leading-none outline-hidden"
      >
        <ComboboxTrigger
          v-required="{
            required: required && !disabled,
            hasValue: !isSelectedEmptyValue,
          }"
          class="grid w-full grid-cols-[auto_min-content] items-center rounded-sm border outline-hidden"
          :class="{
            'bg-neutral-light-10': !disabled,
            'bg-neutral-light-10/50 border-neutral-light-0/50': disabled,
            'border-neutral-light-0': !required && !disabled,
          }"
          :role="mode === 'select' ? 'combobox' : undefined"
        >
          <div
            v-if="mode === 'combobox'"
            class="flex h-full w-full items-center text-xs"
          >
            <Icon
              class="text-neutral-dark-3/60 pr-1.5 pl-2.5"
              size="sm"
              icon="search"
            />
            <ComboboxInput
              v-if="!displayedValue"
              class="placeholder-neutral-dark-3/40 bg-neutral-light-10 mr-0.5 h-full w-full outline-hidden"
              :placeholder
              @keyup.space.prevent
            />
            <!-- @keyup.space.prevent : prevents setting open="false" after pressing space -->
            <slot
              v-else
              name="displayedValue"
              :selectedOption
            >
              <span>{{ displayedValue }}</span>
            </slot>
          </div>

          <div
            v-else
            class="text-neutral-dark-0 h-full w-full items-center truncate pl-1 text-left text-sm leading-[1.9rem] whitespace-nowrap"
            :class="{
              'text-neutral-dark-0/50': disabled,
            }"
          >
            <slot
              name="displayedValue"
              :selectedOption
            >
              {{ displayedValue || placeholder }}
            </slot>
          </div>

          <div class="flex items-center justify-end pr-1">
            <slot name="trigger" />

            <MultiSelectClearButton
              v-if="
                withClear === true &&
                !disabled &&
                selected &&
                !R.isEmpty(selected)
              "
              @click.stop="clearValue"
            />

            <Icon
              v-else-if="mode === 'select'"
              class="text-neutral-dark-0 mr-1.75 ml-2.25"
              :class="{
                'text-neutral-dark-0/50': disabled,
              }"
              size="2xs"
              :icon="open ? 'caret-up' : 'caret-down'"
            />
          </div>
        </ComboboxTrigger>
      </ComboboxAnchor>

      <ComboboxPortal>
        <!--
          hideWhenDetached: true
          - Ensures the combobox options are hidden when it is detached from the DOM.
          - Required because the app uses <KeepAlive> for views. If set to false:
            - An inactive view kept alive may re-render and show its combobox.
            - The combobox, being detached from the DOM, would appear on the active view.
            - This causes it to display combobox options out of context, unrelated to the current view.
        -->
        <ComboboxContent
          class="bg-neutral-light-10 border-neutral-light-6 z-20 w-full overflow-auto rounded-sm rounded-t-none border border-t-0 text-xs"
          hideWhenDetached
          disableOutsidePointerEvents
          position="popper"
          align="start"
        >
          <div
            v-if="withFilter"
            class="flex min-h-8 items-center"
            :style="optionsWidthStyle"
          >
            <Icon
              class="text-neutral-dark-3/60 pr-1.75 pl-2.5"
              size="sm"
              icon="search"
            />
            <ComboboxInput
              v-if="withFilter"
              class="placeholder-neutral-dark-3/40 bg-neutral-light-10 w-full outline-hidden"
              autoFocus
              placeholder="search"
            />
          </div>
          <ComboboxViewport
            class="overflow-x-hidden!"
            :style="optionsWidthStyle"
          >
            <div class="max-h-40 overflow-y-auto">
              <div
                v-if="pendingOptions"
                class="flex h-8 items-center justify-center gap-x-1 text-xs"
              >
                <Icon
                  class="animate-spin"
                  icon="rotate-right"
                />
                Searching...
              </div>

              <template v-else>
                <ComboboxEmpty
                  v-if="searchTerm.length"
                  class="py-2 text-center text-xs"
                >
                  {{ emptyText }}
                </ComboboxEmpty>
                <!-- value: U+0009 CHARACTER TABULATION (to avoid matching value when searching for input)-->
                <ComboboxItem
                  v-if="withEmptyOption"
                  :class="comboboxItemClass"
                  value="	"
                  @select="
                    (event) => {
                      event.preventDefault()
                      clearSelectedValue()
                      open = false
                    }
                  "
                >
                  select
                </ComboboxItem>
                <ComboboxItem
                  v-for="option in filteredOptions"
                  :key="getOptionTextValue(option)"
                  :class="comboboxItemClass"
                  :disabled="isOptionDisabled(option)"
                  :value="getOptionValue(option)"
                >
                  <div
                    class="flex w-full items-center space-x-1.5 overflow-hidden"
                  >
                    <Checkbox
                      v-if="isModeMultiple"
                      :modelValue="hasSelectedOption(option)"
                      size="2xs"
                    />

                    <div class="flex w-full items-center">
                      <span
                        class="flex-1"
                        :class="{
                          'font-bold':
                            !isModeMultiple && hasSelectedOption(option),
                        }"
                      >
                        <slot
                          name="option"
                          :option
                        >
                          {{ getOptionName(option) }}
                        </slot>
                      </span>
                      <MultiSelectClearButton
                        v-if="
                          withClear === 'option' &&
                          !disabled &&
                          hasSelectedOption(option)
                        "
                        @click.stop="clearValue"
                      />
                    </div>
                  </div>
                </ComboboxItem>
              </template>
            </div>
          </ComboboxViewport>
        </ComboboxContent>
      </ComboboxPortal>
    </ComboboxRoot>
  </FormControl>
</template>
